/**
 * Landmark name → MediaPipe index lookup tables.
 *
 * Pure data, no Phaser, no MediaPipe import. Used by the tracker classes
 * to translate friendly names like `'leftWrist'` or `'mouthCenter'` into
 * the integer indices that MediaPipe returns.
 *
 * Sources:
 *   - Pose: https://developers.google.com/mediapipe/solutions/vision/pose_landmarker
 *   - Face: https://developers.google.com/mediapipe/solutions/vision/face_landmarker
 *           (468 landmarks; we expose names for the most useful subset and
 *            ship the full numeric range via getLandmarkByIndex.)
 *   - Hands: https://developers.google.com/mediapipe/solutions/vision/hand_landmarker
 *           (21 landmarks per hand)
 */

/** All 33 pose landmarks (BlazePose topology). */
export const POSE_LANDMARKS = Object.freeze({
  nose: 0,
  leftEyeInner: 1,
  leftEye: 2,
  leftEyeOuter: 3,
  rightEyeInner: 4,
  rightEye: 5,
  rightEyeOuter: 6,
  leftEar: 7,
  rightEar: 8,
  mouthLeft: 9,
  mouthRight: 10,
  leftShoulder: 11,
  rightShoulder: 12,
  leftElbow: 13,
  rightElbow: 14,
  leftWrist: 15,
  rightWrist: 16,
  leftPinky: 17,
  rightPinky: 18,
  leftIndex: 19,
  rightIndex: 20,
  leftThumb: 21,
  rightThumb: 22,
  leftHip: 23,
  rightHip: 24,
  leftKnee: 25,
  rightKnee: 26,
  leftAnkle: 27,
  rightAnkle: 28,
  leftHeel: 29,
  rightHeel: 30,
  leftFootIndex: 31,
  rightFootIndex: 32
});

export const POSE_LANDMARK_COUNT = 33;

/**
 * Useful named subset of the 468 face mesh landmarks. The full set is
 * accessible via `FaceTracker.getLandmarkByIndex(i)`. These names match the
 * landmark indices documented at:
 *   https://github.com/google/mediapipe/blob/master/mediapipe/modules/face_geometry/data/canonical_face_model_uv_visualization.png
 */
export const FACE_LANDMARKS = Object.freeze({
  noseTip: 1,
  noseBottom: 2,
  chinBottom: 152,
  foreheadCenter: 10,
  upperLipTop: 13,
  lowerLipBottom: 14,
  mouthLeft: 61,
  mouthRight: 291,
  mouthCenter: 13,
  leftEyeOuter: 33,
  leftEyeInner: 133,
  leftEyeTop: 159,
  leftEyeBottom: 145,
  rightEyeOuter: 263,
  rightEyeInner: 362,
  rightEyeTop: 386,
  rightEyeBottom: 374,
  leftEyebrowOuter: 70,
  leftEyebrowInner: 107,
  rightEyebrowOuter: 300,
  rightEyebrowInner: 336,
  leftCheek: 234,
  rightCheek: 454,
  leftEarTop: 127,
  rightEarTop: 356
});

export const FACE_LANDMARK_COUNT = 468;

/** All 21 hand landmarks (mirrored across both hands). */
export const HAND_LANDMARKS = Object.freeze({
  wrist: 0,
  thumbCMC: 1,
  thumbMCP: 2,
  thumbIP: 3,
  thumbTip: 4,
  indexMCP: 5,
  indexPIP: 6,
  indexDIP: 7,
  indexTip: 8,
  middleMCP: 9,
  middlePIP: 10,
  middleDIP: 11,
  middleTip: 12,
  ringMCP: 13,
  ringPIP: 14,
  ringDIP: 15,
  ringTip: 16,
  pinkyMCP: 17,
  pinkyPIP: 18,
  pinkyDIP: 19,
  pinkyTip: 20
});

export const HAND_LANDMARK_COUNT = 21;

/** Resolve a friendly name into a numeric index for the given table. */
export function lookupLandmark(table, name) {
  if (typeof name === 'number') return name;
  return Object.prototype.hasOwnProperty.call(table, name) ? table[name] : -1;
}

// ───────────────────────────────────────────────────────── geometry helpers

/** Euclidean distance between two landmarks ({x, y, z?}). */
export function distance(a, b) {
  if (!a || !b) return 0;
  const dx = (a.x || 0) - (b.x || 0);
  const dy = (a.y || 0) - (b.y || 0);
  const dz = (a.z || 0) - (b.z || 0);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Joint angle in degrees at point `b`, formed by the rays b→a and b→c.
 * Returns 0 if any landmark is missing.
 */
export function angle(a, b, c) {
  if (!a || !b || !c) return 0;
  const v1x = (a.x || 0) - (b.x || 0);
  const v1y = (a.y || 0) - (b.y || 0);
  const v2x = (c.x || 0) - (b.x || 0);
  const v2y = (c.y || 0) - (b.y || 0);
  const dot = v1x * v2x + v1y * v2y;
  const m1 = Math.sqrt(v1x * v1x + v1y * v1y);
  const m2 = Math.sqrt(v2x * v2x + v2y * v2y);
  if (m1 === 0 || m2 === 0) return 0;
  let cos = dot / (m1 * m2);
  if (cos > 1) cos = 1;
  if (cos < -1) cos = -1;
  return Math.acos(cos) * (180 / Math.PI);
}

/** Midpoint of two landmarks (for derived points like the body center). */
export function midpoint(a, b) {
  if (!a || !b) return null;
  return {
    x: ((a.x || 0) + (b.x || 0)) / 2,
    y: ((a.y || 0) + (b.y || 0)) / 2,
    z: ((a.z || 0) + (b.z || 0)) / 2
  };
}
